import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Shield, Users, AlertCircle, KeyRound } from 'lucide-react';
import { api } from '../../api/client';
import { useAppContext } from '../../context/AppContext';
import Navbar from '../shared/Navbar';
import Modal from '../shared/Modal';
import ChangePasswordModal from './ChangePasswordModal';

const UserManagement = ({ onNavigateBack }) => {
  const { currentUser } = useAppContext();
  const isAdmin = currentUser.role === 'admin';
  const [users, setUsers] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState('participant');
  const [isLoading, setIsLoading] = useState(false);
  const [changingPasswordFor, setChangingPasswordFor] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const data = await api.adminListUsers();
      setUsers(data);
    } catch (error) {
      console.error('Failed to load users:', error);
      alert('Failed to load users: ' + error.message);
    }
  };

  const handleCreateUser = async () => {
    if (!newUserEmail || !newUserName || !newUserPassword) {
      alert('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    try {
      await api.adminCreateUser({
        email: newUserEmail,
        name: newUserName,
        password: newUserPassword,
        role: newUserRole,
      });

      alert('User created successfully!');
      setShowCreateModal(false);
      setNewUserEmail('');
      setNewUserName('');
      setNewUserPassword('');
      setNewUserRole('participant');
      loadUsers();
    } catch (error) {
      console.error('Failed to create user:', error);
      alert('Failed to create user: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Are you sure you want to delete user ${userEmail}?`)) {
      return;
    }

    try {
      await api.adminDeleteUser(userId);
      alert('User deleted successfully');
      loadUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert('Failed to delete user: ' + error.message);
    }
  };

  const handleUpdateRole = async (userId, currentRole) => {
    const newRole = prompt(
      `Change role for user (current: ${currentRole})\nEnter: participant, facilitator, or admin`,
      currentRole
    );

    if (!newRole || newRole === currentRole) return;

    if (!['participant', 'facilitator', 'admin'].includes(newRole)) {
      alert('Invalid role. Must be: participant, facilitator, or admin');
      return;
    }

    try {
      await api.adminUpdateUserRole(userId, newRole);
      alert('User role updated successfully');
      loadUsers();
    } catch (error) {
      console.error('Failed to update role:', error);
      alert('Failed to update role: ' + error.message);
    }
  };

  const handleLogout = () => {
    api.logout();
    window.location.reload();
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-700';
      case 'facilitator':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar
        title="User Management"
        onLogout={handleLogout}
        showBack={true}
        onBack={onNavigateBack}
      />

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Manage Users</h2>
            <p className="text-gray-600 mt-1">
              {isAdmin ? 'Create, view, and manage user accounts' : 'Create and view user accounts'}
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 bg-[#01006A] text-white px-4 py-2 rounded-lg hover:bg-[#010059]"
          >
            <Plus className="w-5 h-5" />
            <span>Create User</span>
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map(user => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-[#c0c0e6] rounded-full flex items-center justify-center">
                        <span className="text-[#01006A] font-semibold">
                          {user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                    {isAdmin && (
                      <>
                        <button
                          onClick={() => handleUpdateRole(user.id, user.role)}
                          className="text-[#01006A] hover:text-[#000030]"
                          title="Change role"
                        >
                          <Shield className="w-4 h-4 inline" />
                        </button>
                        <button
                          onClick={() => setChangingPasswordFor(user)}
                          className="text-[#01006A] hover:text-[#000030]"
                          title="Change password"
                        >
                          <KeyRound className="w-4 h-4 inline" />
                        </button>
                        {user.id !== currentUser.id && (
                          <button
                            onClick={() => handleDeleteUser(user.id, user.email)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete user"
                          >
                            <Trash2 className="w-4 h-4 inline" />
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {users.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <p>No users found</p>
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New User"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
            <input
              type="text"
              value={newUserName}
              onChange={(e) => setNewUserName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A]"
              placeholder="John Doe"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <input
              type="email"
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A]"
              placeholder="john.doe@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
            <input
              type="password"
              value={newUserPassword}
              onChange={(e) => setNewUserPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A]"
              placeholder="••••••••"
            />
            <p className="text-xs text-gray-500 mt-1">User will use this to login</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
            <select
              value={newUserRole}
              onChange={(e) => setNewUserRole(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A]"
            >
              <option value="participant">Participant</option>
              <option value="facilitator">Facilitator/Moderator</option>
              {isAdmin && <option value="admin">Admin</option>}
            </select>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={handleCreateUser}
              disabled={isLoading}
              className="flex-1 bg-[#01006A] text-white py-2 rounded-lg hover:bg-[#010059] disabled:bg-gray-300"
            >
              {isLoading ? 'Creating...' : 'Create User'}
            </button>
            <button
              onClick={() => {
                setShowCreateModal(false);
                setNewUserEmail('');
                setNewUserName('');
                setNewUserPassword('');
                setNewUserRole('participant');
              }}
              className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      {changingPasswordFor && (
        <ChangePasswordModal
          user={changingPasswordFor}
          onClose={() => setChangingPasswordFor(null)}
        />
      )}
    </div>
  );
};

export default UserManagement;